import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  UserPlus,
  Calendar,
  Lock,
  CheckCircle,
  ArrowRight,
  Menu,
  X,
  ShieldCheck,
  Heart,
  Clock,
  Users,
  Home,
  Stethoscope,
  Activity,
  MessageCircle,
  Phone,
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { ROUTES } from '../constants';

export const Landing: React.FC = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 overflow-hidden">
            <div className="flex-shrink-0 min-w-0">
              <Logo size="lg" />
            </div>

            <nav className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => scrollToSection('how-it-works')}
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
              >
                How It Works
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
              >
                Services
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
              >
                About
              </button>
            </nav>

            <div className="hidden md:flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate(ROUTES.LOGIN)}>
                Log In
              </Button>
              <Button variant="primary" onClick={() => navigate(ROUTES.REGISTER)}>
                Sign Up
              </Button>
            </div>

            <button
              className="md:hidden flex-shrink-0 p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6 text-gray-700" />
              ) : (
                <Menu className="h-6 w-6 text-gray-700" />
              )}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-4 space-y-3">
              <button
                onClick={() => scrollToSection('how-it-works')}
                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg"
              >
                How It Works
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg"
              >
                Services
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg"
              >
                About
              </button>
              <div className="pt-3 border-t border-gray-200 space-y-2">
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => navigate(ROUTES.LOGIN)}
                >
                  Log In
                </Button>
                <Button
                  variant="primary"
                  className="w-full"
                  onClick={() => navigate(ROUTES.REGISTER)}
                >
                  Sign Up
                </Button>
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="pt-16">
        <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 py-12 sm:py-16 lg:py-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center mb-8 sm:mb-12">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight px-2">
                Connecting Families with Quality Care Providers
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 px-4">
                A new platform designed to simplify finding and booking CQC-verified care services across the UK. Create your profile, share your care needs, and get matched with trusted providers.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-md mx-auto mb-8">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => navigate(ROUTES.REGISTER)}
                  className="flex-1"
                >
                  <UserPlus className="mr-2 h-5 w-5" />
                  Get Started
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => scrollToSection('how-it-works')}
                  className="flex-1"
                >
                  Learn More
                </Button>
              </div>

              <div className="flex flex-wrap justify-center gap-4 sm:gap-6 px-4">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <span className="text-xs sm:text-sm font-medium text-gray-700">CQC Verified Providers</span>
                </div>
                <div className="flex items-center gap-2">
                  <Lock className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <span className="text-xs sm:text-sm font-medium text-gray-700">Secure Platform</span>
                </div>
                <div className="flex items-center gap-2">
                  <Heart className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <span className="text-xs sm:text-sm font-medium text-gray-700">Trusted Care</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="how-it-works" className="py-12 sm:py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-12 lg:mb-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-4">
                How It Works
              </h2>
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto px-4">
                Getting started with quality care is simple with our three-step process
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              <Card hover className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 text-blue-600 rounded-full mb-6 text-2xl font-bold">
                  1
                </div>
                <UserPlus className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Create Your Profile
                </h3>
                <p className="text-gray-600">
                  Sign up and complete your profile with your care requirements, preferences, and specific needs for your loved ones.
                </p>
              </Card>

              <Card hover className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 text-blue-600 rounded-full mb-6 text-2xl font-bold">
                  2
                </div>
                <Users className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Get Matched
                </h3>
                <p className="text-gray-600">
                  Browse and connect with CQC-verified care providers that match your specific requirements and location.
                </p>
              </Card>

              <Card hover className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 text-blue-600 rounded-full mb-6 text-2xl font-bold">
                  3
                </div>
                <Calendar className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Book & Manage
                </h3>
                <p className="text-gray-600">
                  Schedule appointments, manage bookings, and communicate with care providers all in one secure platform.
                </p>
              </Card>
            </div>
          </div>
        </section>

        <section id="about" className="py-12 sm:py-16 lg:py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center">
              <div className="order-2 lg:order-1">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                  Trust & Quality at Our Core
                </h2>
                <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8">
                  We're building a platform where every care provider undergoes rigorous verification to ensure your loved ones receive the highest quality care they deserve.
                </p>

                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">CQC Verified Providers</h4>
                      <p className="text-gray-600">All providers must be registered and inspected by the Care Quality Commission</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Secure Platform</h4>
                      <p className="text-gray-600">Bank-level encryption protects all your personal information and communications</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Transparent Matching</h4>
                      <p className="text-gray-600">Clear information about providers, services, and pricing to help you make informed decisions</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Dedicated Support</h4>
                      <p className="text-gray-600">Our team is here to support you throughout your care journey</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative order-1 lg:order-2">
                <img
                  src="https://images.pexels.com/photos/7551585/pexels-photo-7551585.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Quality care"
                  className="rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        <section id="services" className="py-12 sm:py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-10 lg:mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-4">
                Care Services We Offer
              </h2>
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto px-4">
                Connect with qualified providers offering a comprehensive range of care services
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">
              <Card hover className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-full mb-6">
                  <Home className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Domiciliary Care
                </h3>
                <p className="text-gray-600 mb-4">
                  Professional care services delivered in the comfort and familiarity of your own home. Support with daily activities, personal care, and companionship.
                </p>
              </Card>

              <Card hover className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-50 rounded-full mb-6">
                  <Clock className="h-8 w-8 text-amber-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Respite Care
                </h3>
                <p className="text-gray-600 mb-4">
                  Short-term care solutions to give family caregivers a well-deserved break while ensuring continuous quality care.
                </p>
              </Card>

              <Card hover className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-50 rounded-full mb-6">
                  <Activity className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Dementia Care
                </h3>
                <p className="text-gray-600 mb-4">
                  Specialist care and support for individuals living with dementia, provided by trained professionals in a safe environment.
                </p>
              </Card>

              <Card hover className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-50 rounded-full mb-6">
                  <Stethoscope className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Post-Hospital Care
                </h3>
                <p className="text-gray-600 mb-4">
                  Comprehensive support and recovery care following hospital discharge, helping you transition safely back home with professional assistance.
                </p>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-blue-100 mb-6 sm:mb-8">
              Join our platform today and connect with quality care providers
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-lg mx-auto">
              <Button
                variant="ghost"
                size="lg"
                onClick={() => navigate(ROUTES.REGISTER)}
                className="bg-white text-blue-600 hover:bg-blue-50 hover:text-blue-700 shadow-sm"
              >
                Create Account
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate(ROUTES.REGISTER)}
                className="border-white text-white hover:bg-white/10"
              >
                Register as Provider
              </Button>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-gray-900 text-gray-300 py-12 sm:py-14 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-8 sm:gap-10 lg:gap-6 mb-10 sm:mb-12">
            <div className="sm:col-span-2 lg:col-span-2">
              <div className="mb-4 [&_span]:!text-white">
                <Logo size="sm" />
              </div>
              <p className="text-sm text-gray-400 leading-relaxed max-w-xs">
                Connecting families with quality care providers across the UK.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Platform</h4>
              <ul className="space-y-3 text-sm">
                <li>
                  <button onClick={() => scrollToSection('how-it-works')} className="hover:text-white transition-colors">
                    How It Works
                  </button>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">Pricing</a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">Help Center</a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-3 text-sm">
                <li>
                  <Link to={ROUTES.TERMS_OF_SERVICE} className="hover:text-white transition-colors">Terms of Service</Link>
                </li>
                <li>
                  <Link to={ROUTES.PRIVACY_POLICY} className="hover:text-white transition-colors">Privacy Policy</Link>
                </li>
                <li>
                  <Link to={ROUTES.COOKIE_POLICY} className="hover:text-white transition-colors">Cookie Policy</Link>
                </li>
                <li>
                  <Link to={ROUTES.CANCELLATION_POLICY} className="hover:text-white transition-colors">Cancellation Policy</Link>
                </li>
                <li>
                  <Link to={ROUTES.SAFEGUARDING_POLICY} className="hover:text-white transition-colors">Safeguarding Policy</Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Support</h4>
              <ul className="space-y-3 text-sm">
                <li>
                  <Link to={ROUTES.COMPLAINTS_PROCEDURE} className="hover:text-white transition-colors">Complaints Procedure</Link>
                </li>
                <li>
                  <Link to={ROUTES.ACCESSIBILITY} className="hover:text-white transition-colors">Accessibility</Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Contact</h4>
              <ul className="space-y-3 text-sm">
                <li className="whitespace-nowrap">Info@nursingcarebooking.com</li>
                <li>
                  <a href="tel:+447462365503" className="inline-flex items-center gap-1.5 hover:text-white transition-colors">
                    <Phone className="w-3.5 h-3.5" />
                    <span>+44 7462 365503</span>
                  </a>
                </li>
                <li>
                  <a href="https://wa.me/447462365503" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-green-400 transition-colors">
                    <MessageCircle className="w-3.5 h-3.5" />
                    <span>WhatsApp</span>
                  </a>
                </li>
                <li className="pt-1">
                  <span className="text-xs text-gray-500">CQC Partner Platform</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-800 text-sm text-center text-gray-500">
            <p>&copy; {new Date().getFullYear()} NursingCareBooking.com. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};
